// Artwork with an intentional fallback (UX04, SAFE02). Owner: WP04.
// Images only come from the validated same-origin relay GET /api/artwork/{id}; never provider URLs.
import { useState, type ReactNode } from 'react';

export type ArtworkProps = {
  /** Artwork id from the API model (poster, logo, backdrop, still); null/undefined shows the fallback. */
  id: string | null | undefined;
  /** Alt text. Pass '' when the title is already visible next to the image (decorative). */
  alt: string;
  /** Text used for the fallback initials and colour (usually the title or channel name). */
  label: string;
  /** Optional icon shown instead of initials in the fallback. */
  icon?: ReactNode;
  ratio?: '16/9' | '2/3' | '1/1' | '4/3';
  fit?: 'cover' | 'contain';
  /** Fallback without initials/icon (e.g. when a channel logo is already shown elsewhere on the card). */
  bare?: boolean;
  className?: string;
};

const PALETTES = [
  ['#24452f', '#101215'], ['#2a3550', '#101215'], ['#4a2f2a', '#101215'], ['#3b2d4a', '#101215'],
  ['#1f4046', '#101215'], ['#46401f', '#101215'], ['#2f3a2a', '#15181c'], ['#40283a', '#101215'],
];
/** Deterministic, stable colour pick so the same title always gets the same fallback. */
export function fallbackPalette(label: string): string[] {
  let h = 0;
  for (const ch of label) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return PALETTES[h % PALETTES.length]!;
}
export function initials(label: string): string {
  const words = label.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => [...w][0]!.toUpperCase()).join('') || '·';
}

export function artworkUrl(id: string): string {
  return `/api/artwork/${encodeURIComponent(id)}`;
}

export function Artwork({ id, alt, label, icon, ratio, fit = 'cover', bare, className }: ArtworkProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = !!id && failed !== id;
  const [a, b] = fallbackPalette(label);
  return (
    <div className={['ui-art', className ?? ''].filter(Boolean).join(' ')} style={ratio ? { aspectRatio: ratio } : undefined}>
      {showImage ? (
        <img src={artworkUrl(id)} alt={alt} loading="lazy" decoding="async" style={{ objectFit: fit }} onError={() => setFailed(id)} />
      ) : (
        <div className="ui-art__fallback" style={{ background: `linear-gradient(135deg, ${a}, ${b})` }} role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={alt ? undefined : true}>
          {bare ? null : <span aria-hidden>{icon ?? initials(label)}</span>}
        </div>
      )}
    </div>
  );
}
